/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2015, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'jquery',
	'react',
	'react-dom',
	'./commonSelectItemsDialog/CommonSelectItemsComponent'
], function(View, $, React, ReactDOM, CommonSelectItemsComponent) {
	/**
	 * CommonSelectItemsDialog presents a picklist dialog to allow selection of a number of items specified by the object
	 * which creates the dialog. Note, currently we allow only the selection of one item at a time, the reason that the object
	 * is name 'Items' is because it would be very easy to extend this class to include the selection of multiple items and
	 * I believe there will be a need for this in the near future. I leave this as an enhancement with the consuming app
	 * specifying if this is a single or mutli select dialog with an option which is passed in.
	 */
	class CommonSelectItemsDialog extends View {
		/**
		 * Initialize a new picklist dialog with the specified options.
		 * @param options - an object of the following form
		 *		{
		 *			.....					<= see parent class for options not specific to this dialog
		 *			.currentSelectedItem 	<= (optional) the item currently selected (will be checked)
		 *			.possibleItemsToSelect	<= (required) array of possible items that the user can select
		 *										The Possible Items must be of the form:
		 *											{
		 *												id: item id which is passed back to the eventHandler when an item is selected,
		 *												localizedString: localized string which is shown in the dialog
		 *			.eventHandler			<= (required) the object which handles the selection of an item. This object
		 *										must be able to handle a handleSelection(item) call.
		 *		}
		 */
		constructor(...args) {
			super(...args);
			let options = args && args[0];
			// This is necessary to support the new SlotActions and WidgetActions format
			this._selectionHandler = options.actions ? { handleSelection: options.actions.apply } : options.eventHandler;
			options = options.state ? options.state : options;

			// Store options needed specifically for this dialog
			const _sortColumnOrder = options.eventHandler && options.eventHandler._sortColumn && options.eventHandler._sortColumn.order;
			this._currentSelectedItem = _sortColumnOrder || options.currentSelectedItem || options.getCurrentSelectedItem && options.getCurrentSelectedItem() || null;
			this._possibleItemsToSelect = options.possibleItems;
			this.labelsView = options.labelsView;
			this.removeViewWhenApplied = options.removeViewWhenApplied;
		}

		remove() {
			if (this.$el) {
				ReactDOM.unmountComponentAtNode(this.$el[0]);
			}
			this.labelsView = null;
			return super.remove();
		}

		/**
		 * renders the header with column title and icon, content (possible selectable items) and footer
		 */
		render() {
			this.$el
				.addClass('popoverDialogContainer');
			this.reactComponents = ReactDOM.render(<CommonSelectItemsComponent
				possibleItems={this._possibleItemsToSelect}
				currentlySelectedItemId={this._currentSelectedItem}
				selectionHandler={this._selectItem.bind(this)}
			/>,
			this.$el[0]);

			return this;
		}

		preload() {
			return Promise.resolve();
		}

		renderCallBack(toolbar) {
			this.toolbar = toolbar;
		}

		/**
		 * This method will save the selected item, tell the selection handler to handle
		 * selection of the item, and then update the select-able list to ensure highlight correctness.
		 * @param evt - the item clicked
		 */
		_selectItem(id) {
			if (this.removeViewWhenApplied && this.toolbar) {
				this.remove();
				this.toolbar.hide();
			}
			this._selectionHandler.handleSelection(id);
		}
	}

	return CommonSelectItemsDialog;
});
